import React, { useContext, useEffect, useState } from 'react';
import styles from './employee.module.css';
import common from '../../common.module.scss';
import { getEmployees, deleteEmployee } from '../../api/EmployeeApi';
import ConfirmDeleteModal from '../ConfirmDeleteModal.jsx';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Pagination from '../pagination';
import { EmployeeContext } from '../../context/EmployeeContext.jsx';
import useIsMobile from '@/shared/lib/hooks/useIsMobile';
import { RiDeleteBinLine } from "react-icons/ri";
import { GoPencil } from 'react-icons/go';
import { GiSettingsKnobs } from "react-icons/gi";
import FilterModal from "@/shared/ui/FiltersModal";

export default function EmployeeTable() {
    const { employee } = useContext(EmployeeContext);
    const role = employee?.role;

    const [employees, setEmployees] = useState([]);
    const [search, setSearch] = useState('');
    const [sortBy, setSortBy] = useState('');
    const [direction, setDirection] = useState('asc');
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    const [employeeToDelete, setEmployeeToDelete] = useState(null);
    const [, setLoading] = useState(true);
    const [showModalFilters, setShowModalFilters] = useState(false);

    const navigate = useNavigate();
    const isMobile = useIsMobile();

    useEffect(() => {
        fetchEmployees();
    }, [page, search, sortBy, direction]);

    const fetchEmployees = async () => {
        setLoading(true);
        try {
            const params = {
                page,
                size: 10,
                ...(search && { search }),
                ...(sortBy && { sortBy }),
                ...(sortBy && { direction }),
            };
            const res = await getEmployees(params);
            setEmployees(res.data.content || res.data);
            setTotalPages(res.data.totalPages || 1);
        } catch (e) {
            const errorMessage = e?.response?.data?.message || 'Ошибка при загрузке сотрудников';
            toast.error(errorMessage);
            console.error('Ошибка при загрузке сотрудников', e);
        } finally {
            setLoading(false);
        }
    };

    const handleSort = (field) => {
        if (sortBy !== field) {
            setSortBy(field);
            setDirection('desc');
        } else if (direction === 'desc') {
            setDirection('asc');
        } else {
            setSortBy('');
            setDirection('asc'); // сброс
        }
        setPage(0);
    };

    const renderSortArrow = (field) => {
        if (sortBy !== field) return '';
        return direction === 'asc' ? ' ↑' : ' ↓';
    };

    const handleDelete = async () => {
        if (!employeeToDelete) return;
        try {
            await deleteEmployee(employeeToDelete.id);
            setEmployees(prev => prev.filter(e => e.id !== employeeToDelete.id));
        } catch (e) {
            const errorMessage = e?.response?.data?.message || 'Ошибка при удалении сотрудника';
            toast.error(errorMessage);
            console.error('Ошибка при удалении сотрудника', e);
        } finally {
            setEmployeeToDelete(null);
        }
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 0 && newPage < totalPages) setPage(newPage);
    };

    // сортировка через мобильную модалку
    const handleChangeSort = (field, dir) => {
        setSortBy(field);
        setDirection(dir);
        setPage(0);
    };

    const sortOptions = [
        { value: 'id',           label: 'ID' },
        { value: 'name',         label: 'Имя и фамилия' },
        { value: 'phoneNumber',  label: 'Телефон' },
        { value: 'role',         label: 'Роль' },
        { value: 'login',        label: 'Логин' },
    ];

    return (
        <div className={common.container}>
            {isMobile && (<h1 className={common.mobileHeading}>Сотрудники</h1>)}

            <div className={`${styles.topBar} row align-items-center`}>
                <div className="col-md-6 col-10">
                    <input
                        type="text"
                        placeholder="Поиск"
                        className={`form-control w-100 ${styles.searchInput}`}
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setPage(0);
                        }}
                    />
                </div>

                {isMobile && (
                    <div className="col-2 d-flex justify-content-end">
                        <button
                            type="button"
                            className={`${common.iconButton} ms-2`}
                            onClick={() => setShowModalFilters(true)}
                            aria-label="Открыть сортировку"
                        >
                            <GiSettingsKnobs size={20} />
                        </button>
                    </div>
                )}

                {role !== 'ADMIN' && role !== 'MASTER' && !isMobile && (
                    <div className={`${styles.buttons} col-md-6 d-flex justify-content-end mt-3 mt-md-0`}>
                        <button
                            className="service-btn btn-primary"
                            onClick={() => navigate('/admin/employees/create')}
                        >
                            Добавить сотрудника
                        </button>
                    </div>
                )}
            </div>

            <table className={`table ${common.table}`}>
                <thead className={common.tableHeader}>
                <tr>
                    <th className={common.sortable} onClick={() => handleSort('id')}>
                        ID{renderSortArrow('id')}
                    </th>
                    <th className={common.sortable} onClick={() => handleSort('name')}>
                        Имя и фамилия{renderSortArrow('name')}
                    </th>
                    {!isMobile && (
                        <>
                            <th className={common.sortable} onClick={() => handleSort('phoneNumber')}>
                                Телефон{renderSortArrow('phoneNumber')}
                            </th>
                            <th className={common.sortable} onClick={() => handleSort('role')}>
                                Роль{renderSortArrow('role')}
                            </th>
                            <th className={common.sortable} onClick={() => handleSort('login')}>
                                Логин{renderSortArrow('login')}
                            </th>
                        </>
                    )}
                    {role !== 'ADMIN' && role !== 'MASTER' && (
                        <>
                            <th></th>
                            <th></th>
                        </>
                    )}
                </tr>
                </thead>

                <tbody>
                {employees.map((emp, index) => (
                    <tr key={emp.id} className={index % 2 !== 0 ? common.altRow : ''}>
                        <td>{emp.id}</td>
                        <td>
                            <p className="mb-1">{emp.name}</p>
                            {isMobile && (
                                <>
                                    <p className="mb-1">{emp.phoneNumber}</p>
                                    <p className="opacity-50 mb-0">{emp.role}</p>
                                </>
                            )}
                        </td>

                        {!isMobile && (
                            <>
                                <td>{emp.phoneNumber}</td>
                                <td>{emp.role}</td>
                                <td>{emp.login}</td>
                            </>
                        )}

                        {role !== 'ADMIN' && role !== 'MASTER' && (
                            <>
                                <td>
                    <span
                        className="btn-word black"
                        onClick={() => navigate(`/admin/employees/edit/${emp.id}`)}
                    >
                      {isMobile ? <GoPencil className={common.icon} /> : "Редактировать"}
                    </span>
                                </td>
                                <td>
                                    {!emp.isAdmin && (
                                        <span
                                            className={`btn-word red btn-link ${common.editBtn}`}
                                            onClick={() => setEmployeeToDelete(emp)}
                                        >
                        {isMobile ? <RiDeleteBinLine className={common.icon} /> : "Удалить"}
                      </span>
                                    )}
                                </td>
                            </>
                        )}
                    </tr>
                ))}
                </tbody>
            </table>

            <Pagination page={page} totalPages={totalPages} onPageChange={handlePageChange} />

            {role !== 'ADMIN' && role !== 'MASTER' && isMobile && (
                <div className={`${styles.buttons} col-12 d-flex justify-content-end mt-5`}>
                    <button
                        className="service-btn btn-primary w-100"
                        onClick={() => navigate('/admin/employees/create')}
                    >
                        Добавить сотрудника
                    </button>
                </div>
            )}

            {employeeToDelete && (
                <ConfirmDeleteModal
                    roomName={employeeToDelete.name}
                    onConfirm={handleDelete}
                    onCancel={() => setEmployeeToDelete(null)}
                />
            )}

            <FilterModal
                isOpen={showModalFilters}
                onClose={() => setShowModalFilters(false)}
                sortOptions={sortOptions}
                sortBy={sortBy}
                direction={direction}
                onChangeSort={handleChangeSort}
            />
        </div>
    );
}
